import React, { useRef, useEffect } from 'react';

interface Question {
  id: string;
  type: 'yesno' | 'text' | 'select' | 'date' | 'consent';
  title: string;
  description?: string;
  required?: boolean;
  options?: string[];
  validation?: { 
    pattern?: string; 
    minLength?: number; 
    maxLength?: number; 
  };
}

interface QuestionRendererProps {
  question: Question;
  answer: any;
  onChange: (answer: any) => void;
}

export function QuestionRenderer({ question, answer, onChange }: QuestionRendererProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  // Auto-focus on the input when component mounts
  useEffect(() => {
    if (inputRef.current && question.type !== 'yesno' && question.type !== 'consent') {
      inputRef.current.focus();
    }
  }, [question.id]);

  const baseInputClasses = `
    w-full rounded-md px-4 py-3 text-gray-900 bg-white border border-gray-300 
    placeholder-gray-400 transition-all duration-200
    focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:outline-none
    text-lg md:text-xl
  `;

  const buttonClasses = `
    flex-1 py-4 px-6 rounded-lg border-2 transition-all duration-200 font-medium text-lg
    focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2
    min-h-[60px] flex items-center justify-center
  `;

  switch (question.type) {
    case 'yesno':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => onChange(true)}
              className={`
                ${buttonClasses}
                ${answer === true
                  ? 'border-amber-500 bg-amber-50 text-amber-700 shadow-lg'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-amber-300 hover:bg-amber-50'
                }
              `}
            >
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Yes
            </button>
            
            <button
              type="button"
              onClick={() => onChange(false)}
              className={`
                ${buttonClasses}
                ${answer === false
                  ? 'border-red-500 bg-red-50 text-red-700 shadow-lg'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-red-300 hover:bg-red-50'
                }
              `}
            >
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              No
            </button>
          </div>
        </div>
      );

    case 'text':
      const isTextarea = (question.validation?.maxLength || 0) > 100;
      
      return (
        <div className="space-y-2">
          {isTextarea ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={answer || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Enter your answer here..."
              rows={4}
              minLength={question.validation?.minLength}
              maxLength={question.validation?.maxLength}
              className={`${baseInputClasses} resize-none`}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={answer || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Enter your answer here..."
              minLength={question.validation?.minLength}
              maxLength={question.validation?.maxLength}
              pattern={question.validation?.pattern}
              className={baseInputClasses}
            />
          )}
          
          {question.validation?.maxLength && (
            <div className="text-right">
              <span className={`text-sm ${
                (answer?.length || 0) > question.validation.maxLength * 0.9
                  ? 'text-red-500' 
                  : 'text-gray-400'
              }`}>
                {answer?.length || 0} / {question.validation.maxLength}
              </span>
            </div>
          )}
        </div>
      );

    case 'select':
      return (
        <div className="space-y-3">
          {question.options?.map((option, index) => (
            <button
              key={index}
              type="button"
              onClick={() => onChange(option)}
              className={`
                w-full py-4 px-6 rounded-lg border-2 transition-all duration-200 text-left font-medium
                focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2
                min-h-[60px] flex items-center
                ${answer === option
                  ? 'border-amber-500 bg-amber-50 text-amber-700 shadow-lg'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-amber-300 hover:bg-amber-50'
                }
              `}
            >
              <div className="flex items-center w-full">
                <div className={`
                  w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center
                  ${answer === option ? 'border-amber-500' : 'border-gray-300'}
                `}>
                  {answer === option && (
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                  )}
                </div>
                <span className="text-lg">{option}</span>
              </div>
            </button>
          ))}
        </div>
      );

    case 'date':
      return (
        <div className="relative">
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="date"
            value={answer || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseInputClasses} [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
          />
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        </div>
      );

    case 'consent':
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Consent Required</h4>
                <p className="text-blue-800 text-sm leading-relaxed">
                  By proceeding, you consent to the collection, use, and storage of your personal information 
                  for the purposes of providing financial advisory services. Your information will be kept 
                  confidential and secure in accordance with our privacy policy.
                </p>
              </div>
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => onChange(!answer)}
            className={`
              w-full py-4 px-6 rounded-lg border-2 transition-all duration-200 font-medium
              focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2
              min-h-[60px] flex items-center
              ${answer
                ? 'border-green-500 bg-green-50 text-green-700 shadow-lg'
                : 'border-gray-300 bg-white text-gray-700 hover:border-green-300 hover:bg-green-50'
              }
            `}
          >
            <div className="flex items-center w-full">
              <div className={`
                w-6 h-6 rounded border-2 mr-4 flex items-center justify-center
                ${answer ? 'border-green-500 bg-green-500' : 'border-gray-300'}
              `}>
                {answer && (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-lg">
                I consent to the collection and use of my personal information
              </span>
            </div>
          </button>
        </div>
      );

    default:
      return (
        <div className="text-red-500 p-4 bg-red-50 rounded-lg">
          Unsupported question type: {question.type}
        </div>
      );
  }
}